import React, { FC, KeyboardEvent, ReactNode, useState } from 'react';
import MuiTextField, { TextFieldProps } from '@mui/material/TextField';
import MuiTooltip from '@mui/material/Tooltip';
import MuiInputAdornment from '@mui/material/InputAdornment';
import MuiIconButton from '@mui/material/IconButton';
import { checkInputValidity } from '../../utils/formFunctions';
import translate from '@translate';
import { isUndefinedNull } from 'src/utils/isDefined';
import { formatNumber, getDecimalSeparator, parseLocaleNumber } from 'src/utils/formatNumber';
import { LockIcon, WarningIcon } from '../SkladonIcon/SmallIcons';
import { styled, SxProps, Theme } from '@mui/material/styles';
import { IconButton, IconButtonProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export const ClearIcon = styled(IconButton)<IconButtonProps>(({ theme }) => ({
    color: '#0000008a',
    fontSize: '1.5rem',
    border: '0',
    margin: 0,

    padding: '4px',
    marginRight: '4px',
    visibility: 'hidden',
    // paddingLeft: '0px',

    '& > svg': {
        width: '1em',
        height: '1em',
        fontSize: '1.25rem',
    },
}));

export const StyledTextField = styled(MuiTextField)<TextFieldProps>(({ theme }) => ({
    '&:hover .number-filter-clear': {
        visibility: 'visible',
    },

    minWidth: '100px',
}));

export type EndIconTypeProps = {
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    icon: any;
    sx?: SxProps<Theme>;
    sxButton?: SxProps<Theme>;
};
export type TextFieldValueType = string | number | undefined;

export interface TextFieldPropsType {
    value: any;
    onChange?: (value: TextFieldValueType, error: boolean) => void;
    type?: 'text' | 'email' | 'number' | 'numberLocale' | 'password' | 'time' | 'date' | 'datetime';
    variant?: 'filled' | 'outlined' | 'standard';
    size?: 'small' | 'medium';
    error?: boolean;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    multiline?: boolean;
    forceFocus?: boolean;
    label?: any;
    name?: string;
    tooltiop?: string;
    helperText?: string;
    errorHelperText?: string;
    endIcon?: EndIconTypeProps;
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    min?: number;
    max?: number;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    children?: ReactNode;
    select?: boolean;
    minWidth?: number;
    sx?: SxProps<Theme>;
    maxLength?: number;
    step?: string;
    inputProps?: any;
    onFocus?: () => void;
    onBlur?: () => void;
    inputRef?: React.MutableRefObject<null>;
    autofocus?: boolean;
    clearValueUndefined?: boolean;
    onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

export const getEndAdornment = (endIcon: EndIconTypeProps, disabled?: boolean, error?: boolean) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (endIcon.onClick) {
            endIcon.onClick(e);
        } else if (error) {
            handleClickError(e);
        }
    };

    const handleClickError = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (event.currentTarget.parentElement?.parentElement) {
            const parent = event.currentTarget.parentElement?.parentElement as HTMLElement;
            if (parent) {
                parent.click();
            }
        }
    };

    return (
        <MuiInputAdornment position="end" sx={endIcon.sx}>
            <MuiIconButton onClick={handleClick} disabled={disabled} sx={endIcon.sxButton}>
                {endIcon.icon}
            </MuiIconButton>
        </MuiInputAdornment>
    );
};

const TextField: FC<TextFieldPropsType> = (props) => {
    const {
        value,
        onChange,
        type,
        variant,
        size,
        error,
        required,
        disabled,
        fullWidth,
        multiline,
        label,
        name,
        tooltiop,
        helperText,
        errorHelperText,
        endIcon,
        forceFocus,
        min,
        max,
        children,
        select,
        minWidth,
        sx,
        maxLength,
        step,
        onBlur,
        onFocus,
        onKeyDown,
        inputRef,
        autofocus,
        clearValueUndefined,
        onClick,
    } = props;

    const isNumberLocale = type === 'numberLocale';
    const [focused, setFocused] = useState(false);
    const [editValue, setEditValue] = useState<string>('');

    const validateNumberLocale = (raw: string): boolean => {
        if (raw === '') {
            return !required;
        }
        const num = parseLocaleNumber(raw);
        if (Number.isNaN(num)) {
            return false;
        }
        if (min !== undefined && num < min) {
            return false;
        }
        if (max !== undefined && num > max) {
            return false;
        }
        if (step === '1') {
            return Number.isInteger(num);
        }
        return true;
    };

    const getNumberLocaleDisplayValue = (): string | number => {
        if (focused) {
            return editValue;
        }
        if (isUndefinedNull(value) || value === '') {
            return '';
        }
        return formatNumber(value);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) {
            if (select) {
                const selectValue = e.target.value;
                const isEmpty = selectValue === undefined || selectValue === null || selectValue === '';
                onChange(selectValue, !!required && isEmpty);
            } else {
                onChange(e.target.value, !checkInputValidity(e.target));
            }
        }
    };

    const handleNumberLocaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setEditValue(raw);
        if (onChange) {
            if (raw === '') {
                onChange(clearValueUndefined ? undefined : '', !!required);
            } else {
                const parsed = parseLocaleNumber(raw);
                onChange(Number.isNaN(parsed) ? raw : parsed, !validateNumberLocale(raw));
            }
        }
    };

    const getNumberLocaleEditValue = (): string => {
        if (isUndefinedNull(value) || value === '') {
            return '';
        }
        const num = parseLocaleNumber(value);
        if (Number.isNaN(num)) {
            return String(value);
        }
        // String(num) drops trailing decimal zeros (e.g. "825.000000" -> "825")
        return String(num).replace('.', getDecimalSeparator());
    };

    const handleFocus = () => {
        if (isNumberLocale) {
            setFocused(true);
            setEditValue(getNumberLocaleEditValue());
        }
        onFocus?.();
    };

    const handleBlur = () => {
        if (isNumberLocale) {
            setFocused(false);
        }
        onBlur?.();
    };

    let endIconParams: EndIconTypeProps | null = null;
    if (error) {
        if (select) {
            endIconParams = {
                icon: <WarningIcon />,
                sx: { position: 'absolute', right: '30px' },
                sxButton: {
                    padding: '0px 0px 0px 0px',
                    '&.MuiButtonBase-root.MuiIconButton-root': { padding: '0px 0px 0px 0px' },
                },
            };
        } else {
            endIconParams = {
                icon: <WarningIcon />,
            };
        }
    } else if (disabled) {
        endIconParams = { icon: <LockIcon /> };
        // } else if (endIcon) {
        //     endIconParams = endIcon;
    }

    const endAdornment: React.ReactNode | null = endIconParams ? getEndAdornment(endIconParams, disabled, error) : null;

    const InputProps: {
        endAdornment?: React.ReactNode;
        startAdornment?: React.ReactNode;
    } = {};

    if (props.startAdornment) {
        InputProps.startAdornment = props.startAdornment;
    }

    const displayClearIcon = !(required || disabled);

    InputProps.endAdornment = (
        <>
            {props.endAdornment}
            {endAdornment}
            {endIcon && getEndAdornment(endIcon, disabled)}
            {displayClearIcon && (
                <ClearIcon className="number-filter-clear" onClick={() => handleClear()} size="medium">
                    <CloseIcon />
                </ClearIcon>
            )}
        </>
    );

    const handleClear = () => {
        if (isNumberLocale) {
            setEditValue('');
        }
        if (onChange) {
            onChange(clearValueUndefined ? undefined : '', !!props.required);
        }
    };

    let errorText = '';
    if (error) {
        if (errorHelperText) {
            errorText = errorHelperText;
        } else if (required && !value) {
            errorText = translate('textField.requiredError');
        } else {
            errorText = translate('textField.error');
        }
    }

    const inputValue = isNumberLocale
        ? getNumberLocaleDisplayValue()
        : isUndefinedNull(value)
        ? ''
        : value;

    const componentRender = (
        <StyledTextField
            autoFocus={autofocus || false}
            value={inputValue}
            onChange={onChange && (isNumberLocale ? handleNumberLocaleChange : handleChange)}
            type={isNumberLocale ? 'text' : type || 'text'}
            variant={variant || 'outlined'}
            size={size || 'medium'}
            error={error}
            required={required}
            disabled={disabled}
            fullWidth={fullWidth}
            multiline={multiline}
            label={label || (required ? ' ' : undefined)}
            name={name}
            helperText={errorText || helperText || ' '}
            InputLabelProps={{ shrink: true }}
            InputProps={InputProps}
            inputProps={{
                min,
                max,
                maxLength,
                step: type === 'number' ? step || 'any' : undefined,
                inputMode: isNumberLocale ? 'decimal' : props.inputProps?.inputMode,
                ...(props.inputProps || {}),
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            inputRef={inputRef}
            select={select}
            sx={{ minWidth, ...(sx || {}) }}
            className={`${displayClearIcon ? 'has-clear-icon' : ''} ${inputValue ? 'has-value' : ''}`}
            onClick={
                onClick || forceFocus
                    ? (e) => {
                          e.preventDefault();
                      }
                    : select
                    ? (e) => {
                          // if click on select drop icon, don't work with clear icon
                          // @ts-ignore
                          if (name && e.target.classList.contains('MuiInputBase-adornedEnd')) {
                              const element = document
                                  .getElementsByName(name)[0]
                                  .parentElement?.querySelectorAll('[role="button"]')[0];
                              element?.dispatchEvent(
                                  new MouseEvent('mousedown', {
                                      view: window,
                                      bubbles: true,
                                      cancelable: true,
                                  })
                              );
                          }
                      }
                    : undefined
            }
        >
            {children}
        </StyledTextField>
    );

    if (tooltiop) {
        return <MuiTooltip title={tooltiop}>{componentRender}</MuiTooltip>;
    }

    return componentRender;
};

export default TextField;
